import { Fragment, FunctionComponent } from "react";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Paper,
} from "@mui/material";

import { Cell, HeadCell, GrayRow } from "..";
import { formatTime, subtractTime } from "../../utils";
import { formatDelayField } from "./delayDisplay";
import { delayBandCssVar, delayMinutesFromTimestamps, getDelayBand } from "../../utils/delayBand";

const wrappingCellSx = {
  whiteSpace: "normal" as const,
  wordBreak: "break-word" as const,
  overflowWrap: "anywhere" as const,
};

type Props = Time & {
  is_ramp_return?: boolean | number;
  departure_airport: string;
  arrival_airport: string;
  user_tlc: string;
  delays?: Delay[];
};

export const TimeTable: FunctionComponent<Props> = ({
  is_ramp_return,
  departure_airport,
  arrival_airport,
  out_time,
  off_time,
  on_time,
  in_time,
  scheduled_time_of_arrival,
  act_on_time,
  act_out_time,
  act_in_time,
  act_off_time,
  delays = [],
}) => {
  const delayRows = Array.isArray(delays) ? delays : [];
  const departureDelayMinutes = delayMinutesFromTimestamps(out_time, act_out_time);
  const arrivalDelayMinutes = is_ramp_return ? null : delayMinutesFromTimestamps(
    scheduled_time_of_arrival || in_time,
    act_in_time,
  );
  const departureDelayColors = delayBandCssVar(getDelayBand(departureDelayMinutes));
  const arrivalDelayColors = delayBandCssVar(getDelayBand(arrivalDelayMinutes));

  return (
    <Box sx={{ minWidth: 0 }}>
      <TableContainer component={Paper}>
        <Table aria-label="time table">
          <TableHead>
            <TableRow>
              <HeadCell>
                <b>Time</b>
              </HeadCell>
              <HeadCell>Schedule</HeadCell>
              <HeadCell>Actual</HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <GrayRow>
              <Cell component="th" scope="row">
                Departure: <b>{departure_airport}</b>
              </Cell>
              <Cell>{formatTime(out_time)}</Cell>
              <Cell sx={{ color: departureDelayColors.color, fontFamily: "var(--font-mono)" }}>
                {formatTime(act_out_time)}
                {departureDelayMinutes !== null ? ` (${departureDelayMinutes > 0 ? "+" : ""}${Math.round(departureDelayMinutes)} min)` : ""}
              </Cell>
            </GrayRow>

            <TableRow>
              <Cell component="th" scope="row">
                Take-Off:
              </Cell>
              <Cell>{formatTime(off_time)}</Cell>
              <Cell>{formatTime(act_off_time)}</Cell>
            </TableRow>

            <TableRow>
              <Cell component="th" scope="row">
                Landing:
              </Cell>
              <Cell>{formatTime(on_time)}</Cell>
              <Cell>{formatTime(act_on_time)}</Cell>
            </TableRow>

            <GrayRow>
              <Cell component="th" scope="row">
                {is_ramp_return ? "Return to ramp" : <>Arrival: <b>{arrival_airport}</b></>}
              </Cell>
              <Cell>{scheduled_time_of_arrival ? formatTime(scheduled_time_of_arrival) : formatTime(in_time)}</Cell>
              <Cell sx={{ color: arrivalDelayColors.color, fontFamily: "var(--font-mono)" }}>
                {formatTime(act_in_time)}
                {arrivalDelayMinutes !== null ? ` (${arrivalDelayMinutes > 0 ? "+" : ""}${Math.round(arrivalDelayMinutes)} min)` : ""}
              </Cell>
            </GrayRow>

            <TableRow>
              <Cell component="th" scope="row">
                Flight Time:
              </Cell>
              <Cell>{subtractTime(off_time, on_time)}</Cell>
              <Cell>{is_ramp_return ? "Not flown (ramp return)" : subtractTime(act_off_time, act_on_time)}</Cell>
            </TableRow>

            <GrayRow>
              <Cell component="th" scope="row">
                Calculated Block Time:
              </Cell>
              <Cell>{subtractTime(out_time, in_time)}</Cell>
              <Cell>{subtractTime(act_out_time, act_in_time)}</Cell>
            </GrayRow>
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer component={Paper} sx={{ mt: 1.5, minWidth: 0 }}>
        <Table aria-label="delay table">
          <TableBody>
            {delayRows.length ? delayRows.map((delay, index) => (
              <Fragment key={delay.id || `delay-${index}`}>
                <GrayRow>
                  <Cell component="th" scope="row">
                    Delay
                  </Cell>
                  <Cell sx={{ ...wrappingCellSx, color: delayBandCssVar(getDelayBand(Number(delay.minutes))).color, fontFamily: "var(--font-mono)" }}>{formatDelayField(delay.minutes)}</Cell>
                </GrayRow>
                <TableRow>
                  <Cell component="th" scope="row">
                    Delay reason
                  </Cell>
                  <Cell sx={wrappingCellSx}>{formatDelayField(delay.reason)}</Cell>
                </TableRow>
                <GrayRow>
                  <Cell component="th" scope="row">
                    Comment
                  </Cell>
                  <Cell sx={wrappingCellSx}>{formatDelayField(delay.remark)}</Cell>
                </GrayRow>
              </Fragment>
            )) : (
              <TableRow>
                <Cell colSpan={2}>No delay recorded</Cell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
